import { TTemporalDataPoint } from "@/components/ui/chart/types/temporal-chart-series.type";
import { EDataUnit } from "@/constants/data-units.type";
import {
  EMainConsumptionSource,
  TCommonConsumption,
  TWattHourValue,
} from "@/data/api/kili/consumption/types/common-consumption.type";
import { MonositeConsumption } from "@/domains/v4b/site-consumption/monosite-consumption.types";
import {
  ConsumptionPointsValue,
  ConsumptionValue,
  CostValue,
  DeviceCategoryConsumption,
  DeviceCategoryConsumptionValue,
  EvolutionValue,
} from "@/domains/v4b/site-consumption/site-consumption.types";
import { TimePoint } from "@/domains/v4b/site-consumption/time-point-metrics.types";
import { assertUnreachable } from "@/utils/types-utils";

function mapWattHourValue(consumptionValue: ConsumptionValue): TWattHourValue {
  return {
    value: consumptionValue.value,
    unit: EDataUnit.WATT_HOUR,
  };
}

function mapCostValue(costValue: CostValue): { value: number | null; unit: EDataUnit.EURO } {
  return {
    value: costValue.value,
    unit: EDataUnit.EURO,
  };
}

function mapEvolutionValue(consumptionValue: EvolutionValue): { value: number | null; unit: EDataUnit.PERCENT } {
  return {
    value: consumptionValue.value,
    unit: EDataUnit.PERCENT,
  };
}

function mapMainConsumptionSource(
  mainConsumptionSource: MonositeConsumption["mainConsumptionSource"],
): TCommonConsumption["mainConsumptionSource"] {
  if (mainConsumptionSource === "DEVICES") return EMainConsumptionSource.DEVICES;
  if (mainConsumptionSource === "DSO") return EMainConsumptionSource.DSO;

  return assertUnreachable(mainConsumptionSource);
}

function mapTimePoint(timePoint: TimePoint): TTemporalDataPoint {
  return {
    value: timePoint.value,
    date: timePoint.date,
    isComplete: timePoint.isComplete,
  };
}

function mapConsumptionPointsValue(timePointsMetrics: ConsumptionPointsValue): TCommonConsumption["globalByDay"] {
  return {
    value: timePointsMetrics.value.map(mapTimePoint),
    unit: EDataUnit.WATT_HOUR,
  };
}

function mapDeviceCategoryConsumption(applianceCategoryConsumption: DeviceCategoryConsumption): {
  category: string;
  consumption: number | null;
} {
  return {
    category: applianceCategoryConsumption.category,
    consumption: applianceCategoryConsumption.consumption,
  };
}

function mapDeviceCategoryConsumptionValue(
  devicesByCategory: DeviceCategoryConsumptionValue,
): TCommonConsumption["devicesByCategory"] {
  return {
    value: devicesByCategory.value.map(mapDeviceCategoryConsumption),
    unit: EDataUnit.WATT_HOUR,
  };
}

export function mapCommonConsumption(monositeConsumption: MonositeConsumption): TCommonConsumption {
  return {
    global: mapWattHourValue(monositeConsumption.global),
    globalByDay: mapConsumptionPointsValue(monositeConsumption.globalByDay),
    globalCost: mapCostValue(monositeConsumption.globalCost),

    totalDevices: mapWattHourValue(monositeConsumption.global),
    devicesByDay: mapConsumptionPointsValue(monositeConsumption.devicesByDay),
    devicesByCategory: mapDeviceCategoryConsumptionValue(monositeConsumption.devicesByCategory),
    devicesCount: monositeConsumption.devicesCount,

    totalEvolution: mapEvolutionValue(monositeConsumption.totalEvolution),
    totalPerArea: mapWattHourValue(monositeConsumption.global),

    mainConsumptionSource: mapMainConsumptionSource(monositeConsumption.mainConsumptionSource),
  };
}
